/**
 * Common class patterns used across components
 * Use with the `cn()` utility to combine with additional classes
 */

import { extendTailwindMerge } from 'tailwind-merge';

export type ClassValue =
  | ClassArray
  | ClassDictionary
  | string
  | number
  | bigint
  | null
  | boolean
  | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClassDictionary = Record<string, any>;
export type ClassArray = ClassValue[];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: ['rounded-theme', 'rounded-theme-min', 'rounded-theme-max'],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a class value to a string
 * @param mix - The class value to convert
 * @returns The class value as a string
 */
function toVal(mix: ClassValue) {
  let k,
    y,
    str = '';

  if (typeof mix === 'string' || typeof mix === 'number') {
    str += mix;
  } else if (typeof mix === 'object') {
    if (Array.isArray(mix)) {
      const len = mix.length;
      for (k = 0; k < len; k++) {
        if (mix[k]) {
          if ((y = toVal(mix[k]))) {
            if (str) {
              str += ' ';
            }
            str += y;
          }
        }
      }
    } else {
      for (const y in mix) {
        if (mix[y]) {
          if (str) {
            str += ' ';
          }
          str += y;
        }
      }
    }
  }

  return str;
}

/**
 * Combines class values into a single string
 * From https://www.npmjs.com/package/clsx
 * @param inputs - The class values to combine
 * @returns The combined class string
 */
export function clsx(...inputs: ClassValue[]) {
  let i = 0,
    tmp,
    x,
    str = '';
  const len = inputs.length;
  for (; i < len; i++) {
    if ((tmp = inputs[i])) {
      if ((x = toVal(tmp))) {
        if (str) {
          str += ' ';
        }
        str += x;
      }
    }
  }
  return str;
}
